import { useState } from "react";
import { useCalculatorStore, HistoryEntry } from "../store/calculatorStore";
import { useSharingStore } from "../store/sharingStore";
import { ShareButton } from "./ShareDialog";
import { PlotViewer } from "./PlotViewer";
import { MatrixViewer } from "./MatrixViewer";
import type {
  PlotData,
  CalcArray,
  CalcMatrix,
  CalcValue,
} from "@quanta/calcscript";

// Type guard for arrays (1D)
function isArrayValue(value: unknown): value is CalcArray {
  return Array.isArray(value) && value.length > 0 && !Array.isArray(value[0]);
}

// Type guard for matrices (2D)
function isMatrixValue(value: unknown): value is CalcMatrix {
  return Array.isArray(value) && value.length > 0 && Array.isArray(value[0]);
}

interface HistoryPanelProps {
  compact?: boolean;
}

export function HistoryPanel({ compact = false }: HistoryPanelProps) {
  const {
    sessions,
    activeSessionId,
    history,
    clearSessionHistory,
    reloadHistoryEntry,
    deleteHistoryEntry,
    createSequence,
    collapsedPlots,
    togglePlotCollapsed,
    switchSession,
    showArchivedSessions,
    setShowArchivedSessions,
    searchQuery,
    setSearchQuery,
    searchScope,
    setSearchScope,
    searchDateFrom,
    setSearchDateFrom,
    searchDateTo,
    setSearchDateTo,
    searchErrorsOnly,
    setSearchErrorsOnly,
    searchPlotsOnly,
    setSearchPlotsOnly,
    getFilteredHistory,
    archiveSession,
    unarchiveSession,
    deleteSession,
    renameSession,
    duplicateSession,
  } = useCalculatorStore();

  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(
    new Set(),
  );
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");

  // Get filtered history based on search
  const filteredHistory = getFilteredHistory();

  // Get counts per session for display
  const sessionEntryCounts = sessions.reduce(
    (acc, session) => {
      acc[session.id] = history.filter(
        (h) => h.sessionId === session.id,
      ).length;
      return acc;
    },
    {} as Record<string, number>,
  );

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedEntries);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedEntries(newSelected);
  };

  const handleCreateSequence = () => {
    if (selectedEntries.size === 0) return;
    const name = prompt("Enter sequence name:");
    if (name) {
      const orderedIds = history
        .filter((e) => selectedEntries.has(e.id))
        .map((e) => e.id)
        .reverse();
      createSequence(name, orderedIds);
      setSelectedEntries(new Set());
    }
  };

  const formatRelativeDate = (timestamp: number) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffDays = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const handleSessionRename = (sessionId: string) => {
    if (editingName.trim()) {
      renameSession(sessionId, editingName.trim());
    }
    setEditingSessionId(null);
  };

  // Sort sessions: non-archived first, then by position
  const sortedSessions = [...sessions].sort((a, b) => {
    if (a.isArchived !== b.isArchived) return a.isArchived ? 1 : -1;
    return a.position - b.position;
  });

  const visibleSessions = showArchivedSessions
    ? sortedSessions
    : sortedSessions.filter((s) => !s.isArchived);

  const archivedCount = sessions.filter((s) => s.isArchived).length;

  if (compact) {
    return (
      <div className="history-panel compact">
        <h3>Recent History</h3>
        <div className="history-list">
          {history
            .filter((h) => h.sessionId === activeSessionId)
            .slice(0, 20)
            .map((entry) => (
              <HistoryItem
                key={entry.id}
                entry={entry}
                compact
                onReload={() => reloadHistoryEntry(entry.id)}
              />
            ))}
        </div>
      </div>
    );
  }

  return (
    <div className="history-panel with-sidebar">
      {/* Session Sidebar */}
      <aside className="session-sidebar">
        <div className="session-sidebar-header">
          <h3>Sessions</h3>
        </div>

        <div className="session-list">
          {visibleSessions.map((session) => {
            const shareMode =
              useSharingStore.getState().shareConfigs[session.id]?.shareMode ||
              "private";
            const shareClass =
              shareMode === "account"
                ? "shared-account"
                : shareMode === "public"
                  ? "shared-public"
                  : "";
            return (
              <div
                key={session.id}
                className={`session-card ${activeSessionId === session.id ? "active" : ""} ${session.isArchived ? "archived" : ""} ${shareClass}`}
                onClick={() => {
                  switchSession(session.id);
                  setSearchScope("current");
                }}
                style={
                  { "--session-color": session.color } as React.CSSProperties
                }
              >
                <span className="session-card-indicator" />
                <div className="session-card-content">
                  {editingSessionId === session.id ? (
                    <input
                      type="text"
                      className="session-card-input"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onBlur={() => handleSessionRename(session.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSessionRename(session.id);
                        if (e.key === "Escape") setEditingSessionId(null);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  ) : (
                    <span
                      className="session-card-name"
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        setEditingSessionId(session.id);
                        setEditingName(session.name);
                      }}
                    >
                      {session.name}
                    </span>
                  )}
                  <span className="session-card-meta">
                    {formatRelativeDate(session.lastAccessedAt)}
                  </span>
                  <span className="session-card-count">
                    {sessionEntryCounts[session.id] || 0} entries
                  </span>
                </div>
                <div className="session-card-actions">
                  {session.isArchived ? (
                    <button
                      className="session-card-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        unarchiveSession(session.id);
                      }}
                      title="Unarchive"
                    >
                      <UnarchiveIcon />
                    </button>
                  ) : (
                    <button
                      className="session-card-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        archiveSession(session.id);
                      }}
                      title="Archive"
                    >
                      <ArchiveIcon />
                    </button>
                  )}
                  <button
                    className="session-card-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      duplicateSession(session.id);
                    }}
                    title="Duplicate"
                  >
                    <DuplicateIcon />
                  </button>
                  {sessions.filter((s) => !s.isArchived).length > 1 && (
                    <button
                      className="session-card-action delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Delete "${session.name}"?`)) {
                          deleteSession(session.id);
                        }
                      }}
                      title="Delete"
                    >
                      <DeleteIcon />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {archivedCount > 0 && (
          <button
            className="show-archived-btn"
            onClick={() => setShowArchivedSessions(!showArchivedSessions)}
          >
            {showArchivedSessions ? "Hide" : "Show"} Archived ({archivedCount})
          </button>
        )}
      </aside>

      {/* Main History Content */}
      <div className="history-main">
        <div className="history-header">
          <h2>
            {searchScope === "all"
              ? "All Sessions"
              : sessions.find((s) => s.id === activeSessionId)?.name ||
                "History"}
          </h2>
          <div className="history-actions">
            <div className="search-container">
              <input
                type="search"
                placeholder="Search history..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              <select
                value={searchScope}
                onChange={(e) =>
                  setSearchScope(e.target.value as "all" | "current")
                }
                className="scope-select"
              >
                <option value="current">Current Session</option>
                <option value="all">All Sessions</option>
              </select>
            </div>
            <div className="search-filters">
              <label className="filter-date">
                <span>From</span>
                <input
                  type="date"
                  value={searchDateFrom}
                  onChange={(e) => setSearchDateFrom(e.target.value)}
                  className="date-input"
                />
              </label>
              <label className="filter-date">
                <span>To</span>
                <input
                  type="date"
                  value={searchDateTo}
                  onChange={(e) => setSearchDateTo(e.target.value)}
                  className="date-input"
                />
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={searchErrorsOnly}
                  onChange={(e) => setSearchErrorsOnly(e.target.checked)}
                />
                Errors only
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={searchPlotsOnly}
                  onChange={(e) => setSearchPlotsOnly(e.target.checked)}
                />
                Plots only
              </label>
              {(searchDateFrom ||
                searchDateTo ||
                searchErrorsOnly ||
                searchPlotsOnly) && (
                <button
                  className="btn-clear-filters"
                  onClick={() => {
                    setSearchDateFrom("");
                    setSearchDateTo("");
                    setSearchErrorsOnly(false);
                    setSearchPlotsOnly(false);
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
            {activeSessionId && <ShareButton sessionId={activeSessionId} />}
            {selectedEntries.size > 0 && (
              <button className="btn-secondary" onClick={handleCreateSequence}>
                Create Sequence ({selectedEntries.size})
              </button>
            )}
            <button
              className="btn-danger"
              onClick={() => {
                if (window.confirm("Clear all history for this session?")) {
                  clearSessionHistory();
                }
              }}
            >
              Clear Session
            </button>
          </div>
        </div>

        <div className="history-list">
          {filteredHistory.length === 0 ? (
            <div className="empty-state">
              {searchQuery
                ? "No matching calculations found"
                : "No calculations yet"}
            </div>
          ) : (
            filteredHistory.map((entry) => (
              <HistoryItem
                key={entry.id}
                entry={entry}
                selected={selectedEntries.has(entry.id)}
                onSelect={() => toggleSelect(entry.id)}
                onReload={() => reloadHistoryEntry(entry.id)}
                onDelete={() => deleteHistoryEntry(entry.id)}
                isCollapsed={collapsedPlots.includes(entry.id)}
                onToggleCollapse={() => togglePlotCollapsed(entry.id)}
                showSessionName={searchScope === "all"}
                sessionName={
                  sessions.find((s) => s.id === entry.sessionId)?.name
                }
                highlightQuery={searchQuery}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function highlightText(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;
  const regex = new RegExp(
    `(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi",
  );
  const parts = text.split(regex);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} className="search-highlight">
        {part}
      </mark>
    ) : (
      part
    ),
  );
}

interface HistoryItemProps {
  entry: HistoryEntry;
  compact?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  onReload: () => void;
  onDelete?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  showSessionName?: boolean;
  sessionName?: string;
  highlightQuery?: string;
}

function HistoryItem({
  entry,
  compact,
  selected,
  onSelect,
  onReload,
  onDelete,
  isCollapsed,
  onToggleCollapse,
  showSessionName,
  sessionName,
  highlightQuery,
}: HistoryItemProps) {
  const isPlot = entry.type === "plot";
  const isMatrixOrArray = entry.type === "matrix" || entry.type === "array";
  const [viewerData, setViewerData] = useState<{
    name?: string;
    value: CalcMatrix | CalcArray;
  } | null>(null);

  // Extract viewable matrices/arrays from a result
  const extractViewableData = (
    result: CalcValue,
  ): { name?: string; value: CalcMatrix | CalcArray }[] => {
    if (!result) return [];

    // Check for matrix (2D array)
    if (isMatrixValue(result)) {
      return [{ value: result }];
    }

    // Check for array (1D array of numbers)
    if (isArrayValue(result) && typeof result[0] === "number") {
      return [{ value: result }];
    }

    return [];
  };

  const viewableItems = extractViewableData(entry.result as CalcValue);
  const hasViewableData = viewableItems.length > 0;

  if (compact) {
    return (
      <div className="history-item compact" onClick={onReload}>
        <code className="history-input">{entry.input}</code>
        <span className="history-result">= {entry.output}</span>
      </div>
    );
  }

  // Terminal-style layout
  return (
    <div
      className={`terminal-entry ${selected ? "selected" : ""} ${entry.isError ? "error" : ""}`}
    >
      {/* Input line with prompt */}
      <div className="terminal-input-line">
        {onSelect && (
          <input
            type="checkbox"
            checked={selected}
            onChange={onSelect}
            className="terminal-select-checkbox"
          />
        )}
        <span className="terminal-prompt">&gt;</span>
        <code className="terminal-input-text">
          {highlightQuery
            ? highlightText(entry.input, highlightQuery)
            : entry.input}
        </code>
        {showSessionName && sessionName && (
          <span className="terminal-session-badge">{sessionName}</span>
        )}
        <button
          className="terminal-delete-btn"
          onClick={onDelete}
          title="Delete"
        >
          ×
        </button>
      </div>

      {/* Output */}
      {entry.isError ? (
        <div className="terminal-output terminal-error">{entry.output}</div>
      ) : isPlot ? (
        <div className="terminal-output terminal-plot">
          <button className="terminal-plot-toggle" onClick={onToggleCollapse}>
            {isCollapsed ? "▶ Show plot" : "▼ Hide plot"}
          </button>
          {!isCollapsed && entry.result && (
            <PlotViewer data={entry.result as PlotData} />
          )}
        </div>
      ) : isMatrixOrArray ? (
        <div className="terminal-output terminal-matrix">
          <pre>{entry.output}</pre>
          {hasViewableData && (
            <button
              className="terminal-view-btn"
              onClick={() => setViewerData(viewableItems[0])}
            >
              View Full
            </button>
          )}
        </div>
      ) : (
        <div className="terminal-output terminal-result">
          <span className="terminal-equals">=</span>{" "}
          {highlightQuery
            ? highlightText(entry.output, highlightQuery)
            : entry.output}
        </div>
      )}

      {viewerData && (
        <MatrixViewer
          value={viewerData.value}
          name={viewerData.name}
          onClose={() => setViewerData(null)}
        />
      )}
    </div>
  );
}

function DeleteIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    </svg>
  );
}

function ArchiveIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="2" y="4" width="20" height="5" rx="1" />
      <path d="M4 9v9a2 2 0 002 2h12a2 2 0 002-2V9" />
      <path d="M10 13h4" />
    </svg>
  );
}

function UnarchiveIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="2" y="4" width="20" height="5" rx="1" />
      <path d="M4 9v9a2 2 0 002 2h12a2 2 0 002-2V9" />
      <path d="M12 10v6M9 13l3-3 3 3" />
    </svg>
  );
}

function DuplicateIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}
